import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Camera, Info } from "lucide-react";
import { toast } from "sonner";

/**
 * R143 — Ticket-Photo policy toggle.
 *
 * Small settings card admins drop onto the property-settings page.
 * When flipped on, tech complete calls without any attached photo
 * are rejected server-side with 400 error=photo_required.
 */
export default function TicketPhotoPolicyCard() {
  const { propertyId, currentProperty } = useProperty();
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!propertyId) return;
    try {
      const { data } = await api.get(`/admin/properties/${propertyId}/ticket-photo-policy`);
      setState(data);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to load policy");
    }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.put(
        `/admin/properties/${propertyId}/ticket-photo-policy`,
        { require_ticket_photo: next },
      );
      setState((s) => ({ ...s, require_ticket_photo: data.require_ticket_photo }));
      toast.success(next
        ? "Photo now required to close tickets"
        : "Techs can close tickets without a photo again");
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Card className="p-5 border-white/10 bg-card"
          data-testid="ticket-photo-policy-card">
      <div className="flex items-center gap-2 mb-3 overline">
        <Camera className="h-4 w-4 text-orange-400" />
        Photo-required policy
        {state?.require_ticket_photo && (
          <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 ml-1">
            enforced
          </Badge>
        )}
      </div>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0 text-sm">
          <div className="font-medium mb-1">
            Require a photo before completing a ticket
          </div>
          <div className="text-white/60 text-xs">
            When on, the mobile Tech app rejects <b>Complete</b> if there&apos;s
            no photo attached. Lifts the photo-attach-rate KPI on the
            leaderboard and gives you a picture-record for disputes.
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0 pt-1">
          <Label className="text-xs text-white/50">
            {state?.require_ticket_photo ? "On" : "Off"}
          </Label>
          <Switch
            checked={!!state?.require_ticket_photo}
            onCheckedChange={toggle}
            disabled={saving || !state}
            data-testid="ticket-photo-policy-toggle"
          />
        </div>
      </div>
      <div className="mt-3 text-[11px] text-white/40 flex items-center gap-1">
        <Info className="h-3 w-3" />
        Applies to <b>{currentProperty?.name || "this property"}</b> only. Set
        per-community from the property switcher.
      </div>
    </Card>
  );
}
